import { forkJoin as RxForkJoin } from 'rxjs/observable/forkJoin';
import { Component, ViewChild, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { finalize } from 'rxjs/operators';
import * as moment from 'moment';
import {
  isNil as _isNil,
  isEmpty as _isEmpty,
  cloneDeep as _cloneDeep,
  find as _find,
  assign as _assign,
  defer as _defer
} from 'lodash';
import { DateTimeUtils, ObjectUtils, UrlUtils, Const } from '@blcloud/bl-common';
import { BlUrlQuery, BlUrlQueryKey } from '@blcloud/bl-ng-common';
import {
  IContactMemoFixedFormat,
  ILoginUserEmployeeBindModel,
  IContactExecuteResult,
  IContactLostReason,
  IEmployee,
  IWorkingTaskInfo,
  IContactHistory
} from '@blcloud/bl-datamodel';
import { CommonConst } from '@blcloud/bl-datamodel/const/common';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { ContactExecuteResultDiv } from '@blcloud/bl-datamodel/enum/deals/contact-execute-result-div';
import { ContactResultExecutePicDiv } from '@blcloud/bl-datamodel/enum/deals/contact-result-execute-pic-div';
import { WorkStatusDiv } from '@blcloud/bl-datamodel/enum/process/work-status-div';
import { EmployeeResource } from '@blcloud/bl-ng-resource';
import {
  AbstractBlModal,
  AbstractBlForm,
  BlValidatorService,
  FormGroupOption,
  ModalButtonType,
  ModalOption,
  ModalSize,
  ModalReason,
  BeginSearchParam,
  BlModalService,
  BlDialogService,
  BlFormGroup
} from '@blcloud/bl-ng-ui-component';
import {
  IEmployeeGuideData,
  EmployeeDiv,
  FrontDivisionBody,
  EmployeeGuideComponent,
  ContactMemoGuideComponent,
  SendSingleMessageModalComponent,
  ISendSingleMessageModalData
} from '@blcloud/bl-ng-share-module';
import { TransitionDeciderService } from '@blcloud/bl-ng-vehicle-customer-module';
import { ResultFormModalService } from './result-form-modal.service';
import { IScheduleHistory, DEFINE, MESSAGE, IStatusCount } from './result-form-modal.interface';
import { IContactEx } from '../../shared/contact/contact-page.interface';

/**
 * 結果入力モーダル起動データ
 */
export interface IResultFormModalData {
  /** 組織コード */
  organizationCode: string;
  /** コンタクト情報リスト */
  contactList: IContactEx[];
  /** 初期インデックス */
  index: number;
  /** 結果通知サブジェクト */
  resultSubject: Subject<IContactEx>;
  /** コンタクト結果実施担当者区分 */
  contactResultExecutePicDiv?: string;
}

/**
 * 結果入力モーダル
 */
@Component({
  selector: 'app-result-form-modal',
  templateUrl: './result-form-modal.component.html',
  styleUrls: ['./result-form-modal.component.scss'],
  providers: [ResultFormModalService]
})
export class ResultFormModalComponent extends AbstractBlModal implements OnDestroy {

  /** 実施結果ラジオボタン */
  @ViewChild('contactExecuteResult') private contactExecuteResult: AbstractBlForm;

  /** 起動データ */
  data: IResultFormModalData;

  /** フォームグループ */
  _blFormGroup: BlFormGroup;

  /** ログインユーザー */
  _loginUser: ILoginUserEmployeeBindModel;

  /** コンタクト実施結果リスト */
  _contactExecuteResultList: IContactExecuteResult[];

  /** コンタクト失注理由リスト */
  _contactLostReasonList: IContactLostReason[];

  /** 予定履歴リスト(最近のお付き合い) */
  _scheduleHistoryList: IScheduleHistory[];

  /** 実施件数 */
  _statusCount: IStatusCount;

  /** 選択インデックス */
  _selectedIndex: number;

  /** 選択中コンタクト情報 */
  _selectedContact: IContactEx;

  /** 画面表示エンティティ */
  _entity: IWorkingTaskInfo;

  /** 再連絡するフラグ */
  _reContactCheckFlg = false;

  /** 失注フラグ */
  _lostOrderFlg = false;

  /** 遷移先URL取得中フラグ */
  _isGettingUrl = false;

  /** 対象車両管理番号 */
  _targetVehicleMgtNumber = 0;

  /** 通信中フラグ */
  _isConnecting = false;

  /** 契約定数定義 */
  readonly ContractConst = ContractConst;

  /** モーダルボタンタイプ定義 */
  readonly ModalButtonType = ModalButtonType;

  /** ルーティングパス定義 */
  readonly RoutePath = Const.RoutePath;

  /** 定数定義 */
  readonly DEFINE = DEFINE;

  /** 入力検証オプション */
  private formGroupOption: FormGroupOption = {
    applyRules: [
      DEFINE.RULE_KEY.EMPLOYEE_NAME,
      DEFINE.RULE_KEY.EXECUTE_RESULT_CODE,
      DEFINE.RULE_KEY.LOST_REASON_CODE,
      DEFINE.RULE_KEY.COMMENT,
      DEFINE.RULE_KEY.RE_EMPLOYEE_NAME,
      DEFINE.RULE_KEY.RE_SCHEDULE_DATE
    ]
  };

  /** 連絡先種別(電話)区分リスト */
  private contactTypeTelList: FrontDivisionBody[];

  /** 編集判定用オリジナルエンティティ */
  private originalEntity: IWorkingTaskInfo;

  /** 排他エラーメッセージ */
  private readonly CONFLICT_MESSAGE = '対象の予定は編集中または既に結果登録されています。';

  /** コンタクト結果更新エラーメッセージ（共通） */
  private readonly CONTACT_RESULT_ERROR_MESSAGE = 'コンタクト結果の更新に失敗しました。\nしばらくたってからもう一度お試しください。';

  /** コンタクト結果更新エラーメッセージ（対象の予定が作り直されている） */
  private readonly CONTACT_RESULT_ERROR_MESSAGE_RECREATE_SCHEDULE = '対象の予定が更新されているため、コンタクト結果を登録できませんでした。\n一覧を再検索してから、もう一度登録してください。';

  /**
   * コンストラクタ
   * @param service サービス
   * @param transitionDeciderService 遷移先決定サービス
   * @param validatorService 入力検証サービス
   * @param modalService モーダル管理サービス
   * @param dialogService ダイアログ管理サービス
   * @param employeeResource 従業員マスタリソース
   */
  constructor(
    private service: ResultFormModalService,
    private transitionDeciderService: TransitionDeciderService,
    private validatorService: BlValidatorService,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    public employeeResource: EmployeeResource
  ) {
    super();
  }

  ngOnDestroy() {
    this.service.destroy();
  }

  /**
   * @override
   */
  onShown() {
    // コンタクト関連情報を購読
    this.service.relatedContact$.subscribe(
      response => {
        // 情報取得時のコンタクトを開いているなら反映
        if (response.tempId === this._selectedContact.gridData.tempId) {
          this._targetVehicleMgtNumber = response.targetVehicleMgtNumber;
          this._scheduleHistoryList = response.scheduleHistoryList;
        }
      }
    );

    // 実施件数を取得
    this._statusCount = this.service.getStatusCount(this.data.contactList);

    // 初期選択
    this.select(this.data.index);

    // 起動データ取得
    this.showLoading();
    this.service.startUp(this.data.organizationCode, !_isNil(this._selectedContact.contactHistory)).pipe(
      finalize(
        () => {
          this.hideLoading();
        }
      )
    ).subscribe(
      ([loginUser, contactTypeTelList, contactExecuteResultList, contactLostReasonList, rules]) => {
        this._loginUser = loginUser;
        this.contactTypeTelList = contactTypeTelList;
        this._contactExecuteResultList = contactExecuteResultList;
        this._contactLostReasonList = contactLostReasonList;

        this._blFormGroup = this.validatorService.getBlFormGroup(rules, this.formGroupOption);
        this.setContactEmployeeInitialValue();
        this.originalEntity = _cloneDeep(this._entity);
        this.checkLostOrder();
        _defer(() => {
          this.contactExecuteResult.focus();
        });
      }
    );
  }

  /**
   * @override
   */
  defaultOption(): ModalOption {
    return { size: ModalSize.RequiredFull };
  }

  /**
   * @override
   */
  getResults(): number {
    return this._selectedIndex;
  }

  /**
   * @override
   */
  dismiss(reason: ModalReason) {
    if (!this.content.isLoading) {
      this.emitDismissEvent(reason);
    }
  }

  /**
   * 画面遷移イベントハンドラ
   * @param path 遷移パス
   * @param el 起動エレメント
   */
  onOpenWindow(path?: string, el?: HTMLElement) {

    switch (path) {
      // 受付
      case this.RoutePath.RECEPTION:
        // 受付画面へのURLを確定させる
        this._isConnecting = true;
        this.transitionDeciderService.decideReceptionUrl(this._selectedContact, el).subscribe(
          receptionUrl => {
            this.transitionDeciderService.openWindow(receptionUrl);
            this._isConnecting = false;
          }
        );
        break;

      // 予約カレンダー
      case this.RoutePath.RESERVE:
        this.transitionDeciderService.openWindow(UrlUtils.buildUrlPath(`${Const.SpaRoot.RESERVE}${path}`));
        break;

      // 顧客編集
      case this.RoutePath.CUSTOMER:
        const urlQuery = new BlUrlQuery();
        if (this._targetVehicleMgtNumber > 0) {
          urlQuery.setValue(BlUrlQueryKey.VehicleMgtNumber, this._targetVehicleMgtNumber);
        }
        const option = {
          id: this._selectedContact.gridData.customerCode,
          params: urlQuery.getQueryParams()
        };
        this.transitionDeciderService.openWindow(UrlUtils.buildUrlPath(`${Const.SpaRoot.MENU}${path}`, option));
        break;

      // 車両編集
      case this.RoutePath.VEHICLE:
        // 車両画面へのURLを確定させる
        this._isConnecting = true;
        this.transitionDeciderService.decideVehicleUrl(this._selectedContact).subscribe(
          vehicleUrl => {
            this.transitionDeciderService.openWindow(vehicleUrl);
            this._isConnecting = false;
          }
        );
        break;

      // 伝票履歴
      case this.RoutePath.SLIP_HISTORY:
        // 伝票履歴画面へのURLを確定させる
        this._isConnecting = true;
        this.transitionDeciderService.decideSlipHistoryUrl(this._selectedContact).subscribe(
          slipHistoryUrl => {
            this.transitionDeciderService.openWindow(slipHistoryUrl);
            this._isConnecting = false;
          }
        );
        break;

      // 伝票発行
      default:
        // 伝票画面へのURLを確定させる
        this._isConnecting = true;
        this.transitionDeciderService.decideSlipUrl(this._selectedContact).subscribe(
          slipUrl => {
            this.transitionDeciderService.openWindow(slipUrl);
            this._isConnecting = false;
          }
        );
        break;
    }
  }

  /**
   * 1件DM送信モーダル起動イベントハンドラ
   * @param el 起動エレメント
   */
  onShowSendSingleMessageModal(el: HTMLElement) {
    // 車両管理番号を確定する
    this._isConnecting = true;
    this.transitionDeciderService.decideVehicleMgtNumber(
      this._selectedContact.gridData.customerCode,
      this._targetVehicleMgtNumber,
      el
    ).subscribe(
      vehicleMgtNumber => {
        this._isConnecting = false;
        if (!_isNil(vehicleMgtNumber)) {
          // 1件DM送信モーダル起動
          const data: ISendSingleMessageModalData = {
            organizationCode: this.data.organizationCode,
            customerCode: this._selectedContact.gridData.customerCode,
            vehicleMgtNumber: vehicleMgtNumber
          };
          this.modalService.show(SendSingleMessageModalComponent, { data }).subscribe(
            modalRef => {
              modalRef.hide(el);
            }
          );
        }
      }
    );
  }

  /**
   * コンタクト実施結果コード変更イベントハンドラ
   * @param contactExecuteResultCode コンタクト実施結果コード
   */
  onChangeExecuteResultCode(contactExecuteResultCode: number) {
    this._entity.contactExecuteResultCode = contactExecuteResultCode;
    this._entity.contactExecuteResult = _find(this._contactExecuteResultList, executeResult => {
      return executeResult.contactExecuteResultCode === this._entity.contactExecuteResultCode;
    }).contactExecuteResult;

    this.checkLostOrder();
  }

  /**
   * コンタクト失注理由コード変更イベントハンドラ
   * @param contactLostReasonCode コンタクト失注理由コード
   */
  onChangeLostReasonCode(contactLostReasonCode: number) {
    this._entity.contactLostReasonCode = contactLostReasonCode;
    this._entity.contactLostReason = _find(this._contactLostReasonList, lostReason => {
      return lostReason.contactLostReasonCode === this._entity.contactLostReasonCode;
    }).contactLostReason;
  }

  /**
   * 再連絡チェックボックス変更イベントハンドラ
   * @param checked チェック状態
   */
  onChangeReContactCheckFlg(checked: boolean) {
    this._reContactCheckFlg = checked;
    if (checked) {
      this._entity.reContactEmployeeCode = this._loginUser.employeeCode;
      this._entity.reContactEmployeeName = this._loginUser.employeeName;
      this._entity.reContactImplScheduleDate = moment().startOf('day').format(DateTimeUtils.formatString.datetime);
    } else {
      this._entity.reContactEmployeeCode = null;
      this._entity.reContactEmployeeName = null;
      this._entity.reContactImplScheduleDate = null;
    }
  }

  /**
   * 従業員検索前処理イベントハンドラ
   * @param param 検索パラメータ
   */
  onBeginSearchEmployee(param: BeginSearchParam) {
    param.value = `${CommonConst.COMPANY_MGT_CODE_OWN_COMPANY}_${param.value}`;
  }

  /**
   * 従業員検索後処理イベントハンドラ
   * @param employee 従業員マスタ
   */
  onAfterSearchEmployee(employee: IEmployee) {
    if (!_isNil(employee)) {
      this._entity.reContactEmployeeCode = employee.employeeCode;
      this._entity.reContactEmployeeName = employee.employeeName;
    } else {
      this._entity.reContactEmployeeCode = null;
      this._entity.reContactEmployeeName = null;
    }
  }

  /**
   * 実施担当者の従業員検索後処理イベントハンドラ
   * @param employee 従業員マスタ
   */
  onAfterSearchContactEmployee(employee: IEmployee) {
    if (!_isNil(employee)) {
      this._entity.contactEmployeeCode = employee.employeeCode;
      this._entity.contactEmployeeName = employee.employeeName;
    } else {
      this.clearContactEmployee();
    }
  }

  /**
   * 顧客ガイド起動イベントハンドラ
   * @param el 起動エレメント
   */
  onShowEmployeeGuide(el: HTMLElement) {
    const data: IEmployeeGuideData = {
      employeeCode: this._entity.reContactEmployeeCode,
      employeeDivs: [EmployeeDiv.All]
    };
    this.modalService.show(EmployeeGuideComponent, { data }).subscribe(
      modalRef => {
        modalRef.hide(el);
        if (modalRef.reason === ModalReason.Done) {
          const employee = modalRef.getResults() as IEmployee;
          this._entity.reContactEmployeeCode = employee.employeeCode;
          this._entity.reContactEmployeeName = employee.employeeName;
        }
      }
    );
  }

  /**
   * 実施担当者の従業員ガイド起動イベントハンドラ
   * @param el 起動エレメント
   */
  onShowContactEmployeeGuide(el: HTMLElement) {
    const data: IEmployeeGuideData = {
      employeeCode: this._entity.contactEmployeeCode,
      employeeDivs: [EmployeeDiv.All]
    };
    this.modalService.show(EmployeeGuideComponent, { data }).subscribe(
      modalRef => {
        modalRef.hide(el);
        if (modalRef.reason === ModalReason.Done) {
          const employee = modalRef.getResults() as IEmployee;
          this._entity.contactEmployeeCode = employee.employeeCode;
          this._entity.contactEmployeeName = employee.employeeName;
        }
      }
    );
  }

  /**
   * 再連絡予定日時変更イベントハンドラ
   * @param dateTime 日時
   */
  onChangeReContactDateTime(dateTime: string) {
    if (dateTime === DateTimeUtils.initial.iso8601.dateTime) {
      this._entity.reContactImplScheduleDate = null;
    } else {
      this._entity.reContactImplScheduleDate = dateTime;
    }
  }

  /**
   * 定型文ボタンクリックイベントハンドラ
   * @param el 起動エレメント
   */
  onShowContactMemoGuide(el: HTMLElement) {
    this.modalService.show(ContactMemoGuideComponent).subscribe(
      modalRef => {
        modalRef.hide(el);
        if (modalRef.reason === ModalReason.Done) {
          // 選択した定型文
          const format = modalRef.getResults() as IContactMemoFixedFormat;
          if (_isEmpty(this._entity.contactComment)) {
            this._entity.contactComment = format.contactMemoFixedFormat;
          } else {
            this.dialogService.confirm(MESSAGE.CONFIRM_OVERWRITE_MEMO).subscribe(
              dialogRef => {
                dialogRef.hide(el);
                if (dialogRef.reason === ModalReason.Done) {
                  this._entity.contactComment = format.contactMemoFixedFormat;
                }
              }
            );
          }
        }
      }
    );
  }

  /**
   * 実施結果からの画面遷移イベントハンドラ
   * @param contactHistory コンタクト履歴
   * @param el: 起動エレメント
   */
  onOpenExecuteResult(contactHistory: IContactHistory, el: HTMLElement) {
    this._isGettingUrl = true;
    this.service.getTransitionUrl(contactHistory).pipe(
      finalize(
        () => {
          this._isGettingUrl = false;
        }
      )
    ).subscribe(
      url => {
        if (!_isNil(url)) {
          window.open(url, '_blank');
          el.focus();
        }
      }
    );
  }

  /**
   * ページ移動イベントハンドラ
   * @param isNext 次移動フラグ
   */
  onMove(isNext = false) {
    if (!ObjectUtils.equals(this.originalEntity, this._entity)) {
      // 編集されているので保存する
      let save$: Observable<IContactEx>;

      if (!_isNil(this._selectedContact.contactHistory)) {
        // 既に実施済の場合は再度下書き化は行わない
        save$ = this.service.updateContact(this._selectedContact, this._entity);
      } else {
        // 下書き保存
        save$ = this.service.saveDraftContact(this._selectedContact, this._entity);
      }

      this.showLoading();
      save$.pipe(
        finalize(
          () => {
            this.hideLoading();
          }
        )
      ).subscribe(
        contact => {
          this.data.contactList[this._selectedIndex] = contact;
          this.data.resultSubject.next(contact);
          this.select(isNext ? this._selectedIndex + 1 : this._selectedIndex - 1);
        },
        (error) => {
          this.showErrorDialog(error);
        }
      );

    } else {
      this.select(isNext ? this._selectedIndex + 1 : this._selectedIndex - 1);
    }
  }

  /**
   * 結果取消イベントハンドラ
   */
  onCancel() {
    this.dialogService.confirm(MESSAGE.CONFIRM_CANCEL_CONTACT_RESULT).subscribe(
      dialogRef => {
        dialogRef.hide();
        if (dialogRef.reason === ModalReason.Done) {
          // データ取消
          this.showLoading();
          this.service.cancelContact(this._selectedContact).pipe(
            finalize(
              () => {
                this.hideLoading();
              }
            )
          ).subscribe(
            contact => {
              // 取消後のコンタクト情報で表示を更新する
              this.data.contactList[this._selectedIndex] = contact;
              this._selectedContact = contact;
              this.data.resultSubject.next(contact);
              // 実施件数更新
              this._statusCount = this.service.getStatusCount(this.data.contactList);
              // 表示中コンタクトを取消後の状態で再読込する
              this.select(this._selectedIndex);
              this.dialogService.info(MESSAGE.SUCCESS_CANCEL_CONTACT_RESULT).subscribe(
                (dialog) => {
                  dialog.hide();
                }
              );
            },
            (error) => {
              this.showErrorDialog(error);
            }
          );
        }
      }
    );
  }

  /**
   * 登録イベントハンドラ
   */
  onDone() {
    // 入力検証
    if (this._blFormGroup.validate()) {
      this.dialogService.confirm(MESSAGE.CONFIRM_SAVE_CONTACT_RESULT).subscribe(
        dialogRef => {
          dialogRef.hide();
          if (dialogRef.reason === ModalReason.Done) {
            // データ保存
            this.showLoading();
            this.service.updateContact(this._selectedContact, this._entity).pipe(
              finalize(
                () => {
                  this.hideLoading();
                }
              )
            ).subscribe(
              contact => {
                this.data.contactList[this._selectedIndex] = contact;
                this._selectedContact = contact;
                this.originalEntity = _cloneDeep(this._entity);
                this.data.resultSubject.next(contact);
                // 実施件数更新
                this._statusCount = this.service.getStatusCount(this.data.contactList);
                this.dialogService.info(MESSAGE.SUCCESS_SAVE_CONTACT_RESULT).subscribe(
                  (dialog) => {
                    dialog.hide();
                  }
                );
              }, (error) => {
                if (error.status === 409) {
                  this.dialogService.info(this.CONFLICT_MESSAGE).subscribe(
                    (dialog) => {
                      dialog.hide();
                      if (dialog.reason === ModalReason.Done) {
                        this.hideLoading();
                        this.dismiss(dialog.reason);
                      }
                    }
                  );
                } else {
                  this.showErrorDialog(error);
                }
              }
            );
          }
        }
      );
    }
  }

  /**
   * コンタクト更新エラー発生時にダイアログを表示
   * @param error エラー
   */
  private showErrorDialog(error: any): void {
    let message = this.CONTACT_RESULT_ERROR_MESSAGE;
    if (error.status === 404) {
      message = this.CONTACT_RESULT_ERROR_MESSAGE_RECREATE_SCHEDULE;
    }
    this.dialogService.error(message).subscribe(dialog => {
      dialog.hide();
    });
  }

  /**
   * 編集対象データを選択します。
   * @param index 選択インデックス
   */
  private select(index: number): void {
    // 選択変更
    this._selectedIndex = index;
    this._selectedContact = _find(this.data.contactList, (_, idx) => {
      return idx === this._selectedIndex;
    });
    this._targetVehicleMgtNumber = this._selectedContact.gridData.vehicleMgtNumber;

    // 編集用エンティティを生成
    this._entity = this.service.createEntity(this._selectedContact);
    this.setContactEmployeeInitialValue();
    this.originalEntity = _cloneDeep(this._entity); // 編集判定用

    // フラグ更新
    this._reContactCheckFlg = !_isNil(this._entity.reContactEmployeeCode) || !_isNil(this._entity.reContactImplScheduleDate);

    // 実施済判定
    this.checkImplemented();

    // 失注判定
    this.checkLostOrder();

    // 検証状態クリア
    if (!_isNil(this._blFormGroup)) {
      this._blFormGroup.reset();
    }

    // 選択したコンタクト情報の関連データ検索を依頼
    this._scheduleHistoryList = null;
    this.service.requestFetchRelatedContact(this._selectedContact);

    // 組織コードを取得（タスク情報またはコンタクト履歴から）
    const organizationCode = this._selectedContact.taskInfo ? this._selectedContact.taskInfo.organizationCode :
      this._selectedContact.contactHistory ? this._selectedContact.contactHistory.organizationCode : null;

    if (!_isNil(organizationCode) && !_isNil(this._blFormGroup)) {
      // 組織コードに基づき、実施結果リストと失注理由リストを再取得して更新する
      RxForkJoin(
        this.service.searchContactExecuteResultList(organizationCode),
        this.service.searchContactLostReasonList(organizationCode)
      ).subscribe(([contactExecuteResultList, contactLostReasonList]) => {
        this._contactExecuteResultList = contactExecuteResultList;
        this._contactLostReasonList = contactLostReasonList;
        // サービス内部の保持リストも更新（失注判定用）
        this.service.updateContactExecuteResultList(contactExecuteResultList);
        // 失注判定を再実行
        this.checkLostOrder();
      });
    }
  }

  /**
   * コンタクト結果実施担当者区分に応じて実施担当者の初期値を設定します。
   */
  private setContactEmployeeInitialValue(): void {
    // 実施済または下書き済の場合は保存済みの実施担当者を優先する
    if (this.isAlreadyImplementedOrDraft()) {
      return;
    }

    switch (this.data.contactResultExecutePicDiv) {
      case ContactResultExecutePicDiv.LoginUser:
      default:
        if (!_isNil(this._loginUser)) {
          this._entity.contactEmployeeCode = this._loginUser.employeeCode;
          this._entity.contactEmployeeName = this._loginUser.employeeName;
        } else {
          this.clearContactEmployee();
        }
        break;
      case ContactResultExecutePicDiv.ExecutePic:
        if (!_isNil(this._selectedContact.taskInfo)) {
          this._entity.contactEmployeeCode = this._selectedContact.taskInfo.employeeCode;
          this._entity.contactEmployeeName = this._selectedContact.taskInfo.employeeName;
        } else {
          this.clearContactEmployee();
        }
        break;
      case ContactResultExecutePicDiv.brank:
        this.clearContactEmployee();
        break;
    }
  }

  /**
   * 実施担当者をクリアします。
   */
  private clearContactEmployee(): void {
    this._entity.contactEmployeeCode = null;
    this._entity.contactEmployeeName = null;
  }

  /**
   * 実施済または下書き済の場合は保存済みの実施担当者を優先する
   * @returns 実施済または下書き済の場合はtrue
   */
  private isAlreadyImplementedOrDraft(): boolean {
    // コンタクト履歴が存在
    const isAlreadyImplemented = !_isNil(this._selectedContact.contactHistory);
    // タスク情報が存在し、作業中
    const isWorking = !_isNil(this._selectedContact.taskInfo)
      && this._selectedContact.taskInfo.workStatusDiv === WorkStatusDiv.Working;
    // 実施担当者が設定済
    const isContactEmployeeCode = !_isNil(this._entity.contactEmployeeCode) || !_isNil(this._entity.contactEmployeeName);
    return isAlreadyImplemented || (isWorking && isContactEmployeeCode);
  }

  /**
   * 実施済判定を行います。
   */
  private checkImplemented(): void {
    if (!_isNil(this._blFormGroup)) {
      // コンタクト履歴が存在すれば実施済
      const isImplemented = !_isNil(this._selectedContact.contactHistory);

      // 実施済の場合は「再連絡」が表示されないので該当箇所の入力検証は必須にしない
      const employeeNameValidator = this._blFormGroup.getValidator(DEFINE.RULE_KEY.RE_EMPLOYEE_NAME);
      employeeNameValidator.updateRule(_assign({}, employeeNameValidator.getRule(), { requiredInputDiv: !isImplemented }));
      this._blFormGroup.setValidator(DEFINE.RULE_KEY.RE_EMPLOYEE_NAME, employeeNameValidator);

      const scheduleDateValidator = this._blFormGroup.getValidator(DEFINE.RULE_KEY.RE_SCHEDULE_DATE);
      scheduleDateValidator.updateRule(_assign({}, scheduleDateValidator.getRule(), { requiredInputDiv: !isImplemented }));
      this._blFormGroup.setValidator(DEFINE.RULE_KEY.RE_SCHEDULE_DATE, scheduleDateValidator);
    }
  }

  /**
   * 失注判定を行います。
   */
  private checkLostOrder(): void {
    const backUp = this._lostOrderFlg;
    const executeResult = _find(this._contactExecuteResultList, contactExecuteResult => {
      return contactExecuteResult.contactExecuteResultCode === this._entity.contactExecuteResultCode;
    });
    if (!_isNil(executeResult)) {
      this._lostOrderFlg = executeResult.contactExecuteResultDiv === ContactExecuteResultDiv.Lostorder;
    } else {
      this._lostOrderFlg = false;
    }

    if (!_isNil(this._blFormGroup) && this._lostOrderFlg !== backUp) {
      // 失注理由の表示/非表示が変更されたら入力検証更新
      const validator = this._blFormGroup.getValidator(DEFINE.RULE_KEY.LOST_REASON_CODE);
      validator.updateRule(_assign({}, validator.getRule(), { requiredInputDiv: this._lostOrderFlg }));
      this._blFormGroup.setValidator(DEFINE.RULE_KEY.LOST_REASON_CODE, validator);
    }
  }

  /**
   * ステータス名称
   */
  get status(): string {
    let text = '';
    if (!_isNil(this._selectedContact) && !_isNil(DEFINE)) {
      if (!_isNil(this._selectedContact.contactHistory)) {
        text = DEFINE.LABEL.IMPLEMENTED;
      } else {
        if (this._selectedContact.taskInfo.workStatusDiv === WorkStatusDiv.Working) {
          text = DEFINE.LABEL.WORKING;
        } else {
          text = DEFINE.LABEL.NOT_IMPLEMENTED;
        }
      }
    }
    return text;
  }

  /**
   * 連絡先名称
   */
  get contactTypeName(): string {
    let name = '';
    if (!_isNil(this._selectedContact)) {
      const contactType = _find(this.contactTypeTelList, contactTypeTel => {
        return contactTypeTel.code === this._selectedContact.gridData.contactTypeCode;
      });
      if (!_isNil(contactType)) {
        name = `(${contactType.name}) `;
      }
    }
    return name;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/result-form-modal/result-form-modal.component.ts